import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import { Box, Typography } from '@mui/material';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import WidgetContainer from './WidgetContainer';
import { updateLayout } from '../../../services/workspaceService';

export default function WorkspaceGrid({
  workspaceId, widgets, recordings, recordingMetadata, eventsMap,
  rules, activeRules,
  globalRules, globalActiveRuleIds, tabRules, tabActiveRuleIds,
  onToggleGlobalRule, onToggleTabRule, onCreateRule,
  workspaceNotes, workspaceMarkers, tabNotes, tabMarkers,
  onUpdateNotes, onUpdateMarkers, onUpdateTabNotes, onUpdateTabMarkers,
  onRemoveWidget, onUpdateWidget, onLayoutChange,
  otherTabsRules,
}) {
  const [unsaved, setUnsaved] = useState(false);
  const saveTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const { containerRef, width } = useContainerWidth();

  const layout = widgets.map((w) => ({
    i: w.id,
    x: w.position?.x || 0,
    y: w.position?.y || 0,
    w: w.position?.w || 4,
    h: w.position?.h || 3,
    minW: 2,
    minH: 2,
  }));

  const persistLayout = useCallback(async (layoutConfig) => {
    try {
      await updateLayout(workspaceId, layoutConfig);
      setUnsaved(false);
      retryCountRef.current = 0;
    } catch (err) {
      retryCountRef.current += 1;
      const delay = Math.min(1000 * 2 ** retryCountRef.current, 30000);
      saveTimerRef.current = setTimeout(() => persistLayout(layoutConfig), delay);
    }
  }, [workspaceId]);

  const handleLayoutChange = useCallback((newLayout) => {
    setUnsaved(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    const updatedWidgets = widgets.map((w) => {
      const item = newLayout.find((l) => l.i === w.id);
      if (item) {
        return { ...w, position: { x: item.x, y: item.y, w: item.w, h: item.h } };
      }
      return w;
    });

    onLayoutChange(updatedWidgets);

    saveTimerRef.current = setTimeout(() => {
      setUnsaved(false);
    }, 1000);
  }, [widgets, onLayoutChange]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // Memoize widget elements so they don't re-create when WorkspaceGrid
  // re-renders from timeline ticks or other unrelated state changes.
  const widgetElements = useMemo(() =>
    widgets.map((widget) => (
      <div key={widget.id} className="drag-handle">
        <WidgetContainer
          widget={widget}
          recordings={recordings}
          recordingMetadata={recordingMetadata}
          eventsMap={eventsMap}
          rules={rules}
          activeRules={activeRules}
          globalRules={globalRules}
          globalActiveRuleIds={globalActiveRuleIds}
          tabRules={tabRules}
          tabActiveRuleIds={tabActiveRuleIds}
          onToggleGlobalRule={onToggleGlobalRule}
          onToggleTabRule={onToggleTabRule}
          onCreateRule={onCreateRule}
          workspaceNotes={workspaceNotes}
          workspaceMarkers={workspaceMarkers}
          tabNotes={tabNotes}
          tabMarkers={tabMarkers}
          onUpdateNotes={onUpdateNotes}
          onUpdateMarkers={onUpdateMarkers}
          onUpdateTabNotes={onUpdateTabNotes}
          onUpdateTabMarkers={onUpdateTabMarkers}
          onRemove={onRemoveWidget}
          onUpdate={onUpdateWidget}
          otherTabsRules={otherTabsRules}
        />
      </div>
    )),
    [widgets, recordings, recordingMetadata, eventsMap,
     rules, activeRules, globalRules, globalActiveRuleIds,
     tabRules, tabActiveRuleIds,
     onToggleGlobalRule, onToggleTabRule, onCreateRule,
     workspaceNotes, workspaceMarkers, tabNotes, tabMarkers,
     onUpdateNotes, onUpdateMarkers, onUpdateTabNotes, onUpdateTabMarkers,
     onRemoveWidget, onUpdateWidget, otherTabsRules]
  );

  return (
    <Box sx={{ position: 'relative' }} ref={containerRef}>
      {unsaved && (
        <Typography variant="caption" color="warning.main" sx={{ position: 'absolute', top: -20, right: 0 }}>
          Unsaved changes...
        </Typography>
      )}
      {width > 0 && (
        <ResponsiveGridLayout
          width={width}
          layouts={{ lg: layout }}
          breakpoints={{ lg: 1200, md: 996, sm: 768 }}
          cols={{ lg: 12, md: 8, sm: 4 }}
          rowHeight={80}
          onLayoutChange={handleLayoutChange}
          draggableHandle=".drag-handle"
          isResizable
          isDraggable
        >
          {widgetElements}
        </ResponsiveGridLayout>
      )}
    </Box>
  );
}
